import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

export type MediaCarouselOption = {
    id: string;
    label?: string;
    thumbnailUrl?: string;
    blobId?: string;
    rank?: number;
};

type MediaReviewCarouselProps = {
    options: MediaCarouselOption[];
    selectedOptionId: string | null | undefined;
    onSelectOption: (optionId: string) => void;
    renderPreview: (option: MediaCarouselOption) => React.ReactNode;
    renderThumbnail?: (option: MediaCarouselOption) => React.ReactNode;
    introText?: string;
    ariaLabel?: string;
    emptyPreviewLabel?: string;
    /** When true, no option is selected until the user clicks a thumbnail. */
    allowEmptySelection?: boolean;
    onClearSelection?: () => void;
};

export function MediaReviewCarousel({
    options,
    selectedOptionId,
    onSelectOption,
    renderPreview,
    renderThumbnail,
    introText,
    ariaLabel = 'Media review carousel',
    emptyPreviewLabel = 'Select an option',
    allowEmptySelection = false,
    onClearSelection,
}: MediaReviewCarouselProps) {
    const sortedOptions = useMemo(
        () => [...options].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0)),
        [options]
    );

    if (!sortedOptions.length) return null;

    const selectedOption = (() => {
        if (selectedOptionId && sortedOptions.some(option => option.id === selectedOptionId)) {
            return sortedOptions.find(option => option.id === selectedOptionId);
        }
        if (allowEmptySelection) return undefined;
        return sortedOptions[0];
    })();

    return (
        <div className="space-y-2" aria-label={ariaLabel}>
            {introText ? (
                <div className="rounded-md border border-[#1a5172]/30 bg-[#cbe3e2]/60 px-3 py-2 text-center">
                    <p className="text-sm font-medium leading-snug text-[#1a5172]">{introText}</p>
                </div>
            ) : null}

            <div className="flex flex-wrap justify-center gap-2" role="tablist" aria-label="Options">
                {sortedOptions.map((option, index) => {
                    const isSelected = option.id === selectedOption?.id;
                    const optionNumber = index + 1;
                    return (
                        <button
                            key={option.id}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            aria-label={
                                option.label
                                    ? `Option ${optionNumber}: ${option.label}`
                                    : `Option ${optionNumber}`
                            }
                            onClick={() => onSelectOption(option.id)}
                            className={cn(
                                'relative box-content h-[80px] w-[128px] shrink-0 overflow-hidden rounded-md border-2 bg-white transition-colors',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-blue focus-visible:ring-offset-2',
                                isSelected
                                    ? 'border-action-blue shadow-sm'
                                    : 'border-gray-200 hover:border-gray-300'
                            )}
                            title={option.label}
                        >
                            {renderThumbnail ? (
                                renderThumbnail(option) ?? (
                                    <span className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-gray-600">
                                        {option.label || 'Option'}
                                    </span>
                                )
                            ) : option.thumbnailUrl ? (
                                <img
                                    src={option.thumbnailUrl}
                                    alt=""
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <span className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-gray-600">
                                    {option.label || 'Option'}
                                </span>
                            )}
                            <span
                                className={cn(
                                    'pointer-events-none absolute left-1 top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-bold text-white shadow-sm',
                                    isSelected ? 'bg-action-blue' : 'bg-[#1a5172]/90'
                                )}
                                aria-hidden
                            >
                                {optionNumber}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                {selectedOption ? (
                    <div className="bg-white">
                        {onClearSelection ? (
                            <button
                                type="button"
                                onClick={onClearSelection}
                                className={cn(
                                    'absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full',
                                    'border border-gray-200 bg-white/95 text-gray-600 shadow-sm transition-colors',
                                    'hover:bg-white hover:text-gray-900',
                                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-blue focus-visible:ring-offset-2'
                                )}
                                aria-label="Clear video selection"
                            >
                                <X className="h-4 w-4" aria-hidden />
                            </button>
                        ) : null}
                        {renderPreview(selectedOption)}
                    </div>
                ) : (
                    <div className="flex min-h-[220px] items-center justify-center p-6 text-sm font-medium text-gray-500">
                        {emptyPreviewLabel}
                    </div>
                )}
            </div>
        </div>
    );
}
